import { useEffect, Component } from "react";
import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { CartProvider } from "./context/CartContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Home from "./pages/Home";
import Admin from "./pages/Admin";
import Track from "./pages/Track";
import Login from "./pages/Login";
import Account from "./pages/Account";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="min-h-screen bg-obsidian text-starlight flex flex-col items-center justify-center gap-4 p-8 text-center">
          <p className="font-display text-2xl text-ember">Something fizzled out</p>
          <p className="text-muted-night text-sm">Please refresh the page to continue.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser, processed } = useAuth();

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = location.hash;
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/session`, {
      method: "POST",
      credentials: "include",
      headers: { "X-Session-ID": sessionId },
    })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((user) => {
        setUser(user);
        navigate("/account", { replace: true, state: { user } });
      })
      .catch(() => navigate("/login", { replace: true }));
  }, []);

  return (
    <div className="min-h-screen bg-obsidian text-starlight flex items-center justify-center">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-muted-night">Signing you in…</p>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/track" element={<Track />} />
      <Route path="/login" element={<Login />} />
      <Route path="/account" element={<Account />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
        </CartProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}
